import React, { useState, useCallback } from "react";
import { useHttpClient } from "shared/hooks/http-hook";
import Button from "shared/components/FormElements/Button";
import AreYouSureModal from "components/shared/AreYouSureModal";

const sectionStyle = {
  padding: "16px",
  borderRadius: "8px",
  backgroundColor: "#fafafa",
  marginBottom: "16px",
  border: "1px solid #eee",
};

const DraftEditor = ({ outreach, onUpdate }) => {
  const { sendRequest } = useHttpClient();
  const [activeStep, setActiveStep] = useState(1);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [validating, setValidating] = useState(false);

  const step = outreach?.steps?.find((s) => s.stepNumber === activeStep);
  const [to, setTo] = useState(step?.to || "");
  const [subject, setSubject] = useState(step?.subject || "");
  const [body, setBody] = useState(step?.body || "");

  const handleStepChange = useCallback((stepNum) => {
    const s = outreach?.steps?.find((st) => st.stepNumber === stepNum);
    setActiveStep(stepNum);
    setTo(s?.to || "");
    setSubject(s?.subject || "");
    setBody(s?.body || "");
    setEmailValid(null);
  }, [outreach]);

  const handleSaveDraft = useCallback(async () => {
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/outreach/${outreach._id}`,
        "PATCH",
        JSON.stringify({ stepNumber: activeStep, to, subject, body }),
      );
      window.toast.success("Draft saved");
      onUpdate();
    } catch (err) {
      window.toast.error("Save failed");
    }
  }, [sendRequest, outreach, activeStep, to, subject, body, onUpdate]);

  const handleSend = useCallback(async () => {
    setSending(true);
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/outreach/${outreach._id}/send`,
        "POST",
        JSON.stringify({ stepNumber: activeStep }),
      );
      window.toast.success("Email queued — will be sent within the hour");
      onUpdate();
    } catch (err) {
      window.toast.error("Queue failed: " + (err.message || ""));
    }
    setSending(false);
    setShowSendModal(false);
  }, [sendRequest, outreach, activeStep, onUpdate]);

  const handlePause = useCallback(async () => {
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/outreach/${outreach._id}/pause`, "POST");
      window.toast.success("Outreach paused");
      onUpdate();
    } catch (err) {
      window.toast.error("Pause failed");
    }
  }, [sendRequest, outreach, onUpdate]);

  const handleResume = useCallback(async () => {
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/outreach/${outreach._id}/resume`, "POST");
      window.toast.success("Outreach resumed");
      onUpdate();
    } catch (err) {
      window.toast.error("Resume failed");
    }
  }, [sendRequest, outreach, onUpdate]);

  const handleValidateEmail = useCallback(async () => {
    if (!to) return;
    setValidating(true);
    try {
      const result = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/admin/lead-hunter/validate-email`,
        "POST",
        JSON.stringify({ email: to }),
      );
      setEmailValid(result);
      if (result.valid) {
        window.toast.success("Email verified (MX records found)");
      } else {
        window.toast.error("Invalid email: " + result.reason);
      }
    } catch (err) {
      setEmailValid({ valid: false, reason: err.message || "Validation failed" });
      window.toast.error("Validation failed");
    }
    setValidating(false);
  }, [sendRequest, to]);

  const handleToChange = useCallback((e) => {
    setTo(e.target.value);
    setEmailValid(null);
  }, []);

  if (!outreach) return null;

  const isContactForm = step?.type === "contact_form";
  const canSend = step && step.status === "DRAFT" && to && subject && body && !isContactForm;
  const isPaused = outreach.status === "PAUSED";
  const isActive = outreach.status === "ACTIVE";
  const inputStyle = { width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" };

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(body);
    window.toast.success("Message copied to clipboard");
  };

  return (
    <div style={sectionStyle}>
      <h4 style={{ marginTop: 0 }}>{isContactForm ? "Contact Form Message" : "Email Editor"}</h4>

      {/* Step tabs */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        {(outreach.steps || []).map((s) => (
          <button
            key={s.stepNumber}
            onClick={() => handleStepChange(s.stepNumber)}
            style={{
              padding: "6px 12px",
              border: activeStep === s.stepNumber ? "2px solid #1976d2" : "1px solid #ccc",
              borderRadius: "4px",
              backgroundColor: activeStep === s.stepNumber ? "#e3f2fd" : "#fff",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Step {s.stepNumber}: {s.type.replace(/_/g, " ")}
            {s.status === "SENT" && " (sent)"}
            {s.status === "SCHEDULED" && " (scheduled)"}
            {s.status === "CANCELLED" && " (cancelled)"}
          </button>
        ))}
      </div>

      {step && isContactForm && (
        <>
          {step.to && (
            <div style={{ marginBottom: "12px", padding: "8px", backgroundColor: "#e3f2fd", borderRadius: "4px" }}>
              <strong style={{ fontSize: "13px" }}>Contact form:</strong>{" "}
              <a href={step.to} target="_blank" rel="noopener noreferrer" style={{ fontSize: "13px", color: "#1976d2" }}>
                {step.to.length > 60 ? step.to.slice(0, 60) + "..." : step.to}
              </a>
            </div>
          )}
          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "13px", color: "#666" }}>Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
            />
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button onClick={handleSaveDraft} size="small">Save Draft</Button>
            <Button onClick={handleCopyMessage} size="small" inverse>Copy Message</Button>
          </div>
          <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
            Copy this message, open the contact form, paste and submit. Then use "Mark Contacted" to update the status.
          </p>
        </>
      )}

      {step && !isContactForm && (
        <>
          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "13px", color: "#666" }}>To</label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input value={to} onChange={handleToChange} style={{ ...inputStyle, flex: 1 }} disabled={step.status === "SENT" || step.status === "SCHEDULED"} />
              {step.status !== "SENT" && step.status !== "SCHEDULED" && to && (
                <button
                  onClick={handleValidateEmail}
                  disabled={validating}
                  style={{
                    padding: "8px 12px", borderRadius: "4px", cursor: "pointer", fontSize: "12px",
                    border: "1px solid #ccc", backgroundColor: emailValid?.valid ? "#e8f5e9" : "#fff",
                  }}
                >
                  {validating ? "..." : "Verify"}
                </button>
              )}
            </div>
            {emailValid && (
              <p style={{ fontSize: "12px", margin: "4px 0 0 0", color: emailValid.valid ? "#2e7d32" : "#d32f2f" }}>
                {emailValid.valid ? `Valid (MX: ${emailValid.mxHost})` : emailValid.reason}
              </p>
            )}
          </div>
          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "13px", color: "#666" }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} style={inputStyle} disabled={step.status === "SENT" || step.status === "SCHEDULED"} />
          </div>
          <div style={{ marginBottom: "8px" }}>
            <label style={{ fontSize: "13px", color: "#666" }}>Body</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
              disabled={step.status === "SENT" || step.status === "SCHEDULED"}
            />
          </div>

          {step.status !== "SENT" && step.status !== "SCHEDULED" && (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button onClick={handleSaveDraft} size="small">Save Draft</Button>
              {canSend && (
                <Button onClick={() => setShowSendModal(true)} size="small" disabled={sending}>
                  {sending ? "Queueing..." : "Queue for Sending"}
                </Button>
              )}
            </div>
          )}

          {(isActive || isPaused) && (
            <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
              {isActive && <Button onClick={handlePause} size="small" inverse>Pause Sequence</Button>}
              {isPaused && <Button onClick={handleResume} size="small">Resume Sequence</Button>}
            </div>
          )}
        </>
      )}

      <AreYouSureModal
        showModal={showSendModal}
        title="Queue Email"
        text={`Queue this email to ${to}? It will be sent within the next hour by the system.`}
        onClose={() => setShowSendModal(false)}
        onConfirmed={handleSend}
      />
    </div>
  );
};

export default DraftEditor;
